import React, { type ReactNode } from "react";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { cn } from "@/lib/utils";
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

interface NumberInputRowProps {
  id: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  unit?: string;
  unitPosition?: "left" | "right";
  placeholder?: string;
  /* Props coming from validation hook (useOptionalFieldValidation or legacy useValidatedInput) */
  inputProps: Record<string, unknown>;
  errorElement?: React.ReactNode;
  className?: string;
  /** Whether the label should prevent wrapping */
  noWrap?: boolean;
  /** Custom width for the input container */
  inputWidth?: "half" | "full";
}

export const NumberInputRow: React.FC<NumberInputRowProps> = ({
  id,
  label,
  description,
  unit,
  unitPosition = "right",
  placeholder,
  inputProps,
  errorElement,
  className,
  noWrap,
  inputWidth = "full",
}) => (
  <div className={cn(
    "grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center",
    "md:flex md:flex-row md:items-center md:gap-4",
    inputWidth === "half" ? "md:justify-start" : "md:justify-between",
    className
  )}>
    <div className={cn(
      "min-w-0 flex flex-col gap-1",
      inputWidth === "half" ? "md:w-1/2" : ""
    )}>
      <Label htmlFor={id} className={cn(fieldLabelClass, { "whitespace-nowrap": noWrap })}>
        {label}
      </Label>
      {description && (
        <p className={cn(fieldDescriptionClass, "hidden md:block")}>
          {description}
        </p>
      )}
    </div>
    <div className={cn(
      "flex flex-col items-end",
      inputWidth === "half" ? "md:w-1/2 md:items-end" : "md:w-auto md:items-end"
    )}>
      <div className={cn(
        "relative",
        inputWidth === "half" ? "w-[100px] md:max-w-[100px]" : "w-[120px] md:max-w-[100px]"
      )}>
        <NumberInput
          {...inputProps}
          className={cn("w-full input-recessed", (inputProps as Record<string, unknown>)?.className as string | undefined)}
          id={id}
          unit={unit}
          unitPosition={unitPosition}
          placeholder={placeholder}
          size="xs"
        />
      </div>
      {errorElement && (
        <div className={cn(
          "mt-1 w-full",
          inputWidth === "half" ? "max-w-[100px]" : "max-w-[120px] md:max-w-[100px]"
        )} aria-live="polite" role="status">
          {errorElement}
        </div>
      )}
    </div>
    {description && (
      <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
        {description}
      </p>
    )}
  </div>
);
